﻿import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AdminErrorBanner, AdminFilterChips, AdminInfoCard, AdminPageLayout } from "@/components/admin";
import DeviceSummaryCards from "./components/DeviceSummaryCards";
import DeviceList from "./components/DeviceList";
import DeviceDetailPanel from "./components/DeviceDetailPanel";
import StatusLogCard from "./components/StatusLogCard";
import { fetchDeviceDetail, fetchDevices, fetchDeviceStatusLogs, fetchSeniorConnectionEvents, updateDeviceStatus, } from "../../../lib/api";
import type { AdminDeviceConnectionEvent, DeviceDetail, DeviceItem, DeviceStatusLogItem } from "@/types.ts";
type DeviceFilter = "all" | "ACTIVE" | "INACTIVE" | "REVOKED";
const countByStatus = (devices: DeviceItem[], status: string) => {
    return devices.filter((device) => device.status === status).length;
};
const DevicesPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const seniorFilterId = Number(searchParams.get("seniorUserAccountId"));
    const queryDeviceId = Number(searchParams.get("deviceId"));
    const [deviceList, setDeviceList] = useState<DeviceItem[]>([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
    const [selectedDevice, setSelectedDevice] = useState<DeviceDetail | null>(null);
    const [statusLogs, setStatusLogs] = useState<DeviceStatusLogItem[]>([]);
    const [connectionEvents, setConnectionEvents] = useState<AdminDeviceConnectionEvent[]>([]);
    const [connectionEventsError, setConnectionEventsError] = useState<string | null>(null);
    const [activeFilter, setActiveFilter] = useState<DeviceFilter>("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [loadingList, setLoadingList] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [auditMessage, setAuditMessage] = useState<string | null>(null);
    const loadList = useCallback(async () => {
        setLoadingList(true);
        setErrorMessage(null);
        try {
            const nextItems = await fetchDevices({
                seniorUserAccountId: Number.isFinite(seniorFilterId) && seniorFilterId > 0 ? seniorFilterId : undefined,
            });
            setDeviceList(nextItems);
            setSelectedDeviceId((current) => current && nextItems.some((item) => item.deviceId === current) ? current : nextItems[0]?.deviceId ?? null);
        }
        catch (error) {
            setErrorMessage(error instanceof Error ? error.message : "기기 목록을 불러오지 못했습니다.");
        }
        finally {
            setLoadingList(false);
        }
    }, [seniorFilterId]);
    const loadDetail = useCallback(async (deviceId: number) => {
        setLoadingDetail(true);
        setErrorMessage(null);
        try {
            const [detail, logs] = await Promise.all([
                fetchDeviceDetail(deviceId),
                fetchDeviceStatusLogs(deviceId),
            ]);
            const eventResult = detail.seniorUserAccountId > 0
                ? await fetchSeniorConnectionEvents(detail.seniorUserAccountId).then((items) => ({ items, error: null })).catch((error: unknown) => ({
                    items: [],
                    error: error instanceof Error ? error.message : "기기 연결 이력을 불러오지 못했습니다.",
                }))
                : { items: [], error: null };
            setSelectedDevice(detail);
            setStatusLogs(logs);
            setConnectionEvents(eventResult.items.filter((event) => event.deviceId === deviceId));
            setConnectionEventsError(eventResult.error);
        }
        catch (error) {
            setErrorMessage(error instanceof Error ? error.message : "기기 상세 정보를 불러오지 못했습니다.");
            setSelectedDevice(null);
            setStatusLogs([]);
            setConnectionEvents([]);
            setConnectionEventsError(null);
        }
        finally {
            setLoadingDetail(false);
        }
    }, []);
    useEffect(() => {
        void loadList();
    }, [loadList]);
    useEffect(() => {
        if (Number.isFinite(queryDeviceId) && queryDeviceId > 0) {
            setSelectedDeviceId(queryDeviceId);
        }
    }, [queryDeviceId]);
    useEffect(() => {
        if (selectedDeviceId == null) {
            setSelectedDevice(null);
            setStatusLogs([]);
            setConnectionEvents([]);
            setConnectionEventsError(null);
            return;
        }
        void loadDetail(selectedDeviceId);
    }, [selectedDeviceId, loadDetail]);
    useEffect(() => {
        const state = location.state as {
            fromLifeSignalDetail?: boolean;
            fromSoundDetail?: boolean;
            elderName?: string;
            elderLoginId?: string;
            selectedDevice?: string;
            selectedDeviceUuid?: string;
        } | null;
        if (!state?.fromLifeSignalDetail && !state?.fromSoundDetail)
            return;
        const queryParts = [state.elderName, state.elderLoginId, state.selectedDevice, state.selectedDeviceUuid].filter((value): value is string => Boolean(value));
        if (queryParts.length > 0) {
            setSearchQuery(queryParts.join(" "));
        }
        navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }, [location.pathname, location.search, location.state, navigate]);
    const summary = useMemo(() => {
        return {
            total: deviceList.length,
            active: countByStatus(deviceList, "ACTIVE"),
            inactive: countByStatus(deviceList, "INACTIVE"),
            revoked: countByStatus(deviceList, "REVOKED"),
        };
    }, [deviceList]);
    const quickFilters: {
        key: DeviceFilter;
        label: string;
        count: number;
    }[] = [
        { key: "all", label: "전체", count: summary.total },
        { key: "ACTIVE", label: "활성", count: summary.active },
        { key: "INACTIVE", label: "비활성", count: summary.inactive },
        { key: "REVOKED", label: "해제", count: summary.revoked },
    ];
    const handleSelect = useCallback((device: DeviceItem) => {
        setSelectedDeviceId(device.deviceId);
    }, []);
    const handleStatusChange = useCallback(async (nextStatus: string, reasonCode: string, reason: string) => {
        if (!selectedDevice || nextStatus === selectedDevice.status)
            return;
        setSaving(true);
        setErrorMessage(null);
        try {
            const result = await updateDeviceStatus(selectedDevice.deviceId, nextStatus, reasonCode, reason);
            setAuditMessage(`감사 #${result.audit.auditLogId} · ${result.audit.beforeStatus} → ${result.audit.afterStatus}`);
            await Promise.all([loadList(), loadDetail(selectedDevice.deviceId)]);
        }
        catch (error) {
            setErrorMessage(error instanceof Error ? error.message : "기기 상태 변경에 실패했습니다.");
        }
        finally {
            setSaving(false);
        }
    }, [loadDetail, loadList, selectedDevice]);
    const handleClose = useCallback(() => {
        setSelectedDeviceId(null);
    }, []);
    const pageSubtitle = Number.isFinite(seniorFilterId) && seniorFilterId > 0
        ? `Senior ID ${seniorFilterId} 연결 기기와 상태 로그 확인`
        : "Senior 연결 기기와 상태 로그 확인";
    return (<AdminPageLayout title="기기 관리" subtitle={pageSubtitle}>
          <AdminErrorBanner message={errorMessage}/>

          <div className="mb-4">
            <DeviceSummaryCards devices={deviceList}/>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
            <div className="xl:col-span-3 flex flex-col gap-3">
              <DeviceList devices={deviceList} selectedDeviceId={selectedDeviceId} searchQuery={searchQuery} activeFilter={activeFilter} totalCount={deviceList.length} loading={loadingList} onSearchChange={setSearchQuery} onSelect={handleSelect} onRefreshClick={loadList}/>
            </div>

            <div className="xl:col-span-1 flex flex-col gap-4">
              <DeviceDetailPanel device={selectedDevice} logs={statusLogs} connectionEvents={connectionEvents} connectionEventsError={connectionEventsError} loading={loadingDetail} saving={saving} auditMessage={auditMessage} onClose={handleClose} onRefresh={() => selectedDeviceId != null && void loadDetail(selectedDeviceId)} onStatusChange={handleStatusChange}/>

              <StatusLogCard selectedDeviceId={selectedDeviceId} logs={statusLogs}/>

              <AdminFilterChips title="빠른 필터" chips={quickFilters} activeKey={activeFilter} onChange={(key) => setActiveFilter(key as DeviceFilter)}/>

              <AdminInfoCard title="기기 관리 기준" description="기기는 어르신 계정에 연결된 등록 단위입니다. 관리자는 상태와 최근 로그를 확인하고, 운영 필요에 따라 활성·비활성·해제 상태를 변경합니다." icon="ri-information-line" tone="slate"/>
            </div>
          </div>
    </AdminPageLayout>);
};
export default DevicesPage;
